import * as React from 'react';
import {useState, useEffect} from 'react';
import {isAfter} from 'date-fns';

import {Reservation} from '../../shared/CommonInterfaces';
import ContainersService from '../../shared/services/ContainersService';
import {CompleteDropoffModal} from '../../dropoffs/shared/CompleteDropoffModal';
import {Inbound, InboundPackagingMap, SkuContent} from '../shared/InboundInterfaces';

export interface CompleteInboundProps {
  inbound: Inbound;
  containersService: ContainersService;
  skuContents: SkuContent[];
  isBolUploaded: boolean;
  reservation: Reservation;
  handleErrors(errors: string[], response);
  reloadContainer();
}

const CompleteInbound: React.FC<CompleteInboundProps> = (props) => {
  const {inbound, containersService, skuContents, isBolUploaded, reservation, handleErrors, reloadContainer} = props;

  const [showModal, setShowModal] = useState(false);
  const [disableComplete, setDisableComplete] = useState(false);
  const [areAllQuantitiesCorrect, setAreAllQuantitiesCorrect] = useState(false);
  const [isShippableSameToExpected, setIsShippableSameToExpected] = useState(false);

  useEffect(() => {
    let totalReceivedQty = 0;
    let totalExpectedQty = 0;

    skuContents.forEach((sc: SkuContent) => {
      totalExpectedQty += sc.expectedQuantity?.amount || 0;
      sc.receivedQuantities.forEach((rq) => {
        totalReceivedQty += rq.amount;
      });
    });

    setIsShippableSameToExpected(totalReceivedQty === totalExpectedQty);
  }, [skuContents]);

  const areShippableItemsPresent = (): boolean => {
    return skuContents.some((item) => {
      const receivedQuantity = item.receivedQuantities.reduce((acc, curr) => acc + curr.amount, 0);
      return receivedQuantity > 0;
    });
  };

  const completeAndFinalizeShipment = async () => {
    if (areShippableItemsPresent()) {
      const response = await containersService.completeAndFinalizeShipment({
        id: inbound.id,
        locationId: null,
        reservationId: reservation.id,
        actualArrivalTime: inbound.actualArrivalTime,
        actualInboundPackagingId: InboundPackagingMap.get(inbound.actualInboundPackaging)
      });
      if (response && response.errors.length === 0) {
        reloadContainer();
      } else {
        setDisableComplete(false);
        const errors = [`There was an error completing inbound #${inbound.id}`];
        handleErrors(errors, response);
      }
    } else {
      setDisableComplete(false);
      const error = 'Inbound quantities must be specified. ';
      handleErrors([error], null);
    }

    setShowModal(false);
  };

  const handleConfirmOrCompleteClick = () => {
    setDisableComplete(true);
    const moveDate = inbound.moveDate;

    const today = new Date();
    if (isAfter(new Date(moveDate), today)) {
      setShowModal(true);
    } else {
      completeAndFinalizeShipment();
    }
  };

  const mismatchQuantityCheckbox = (): JSX.Element => {
    if (!isShippableSameToExpected) {
      return (
        <div className="checkbox">
          <label>
            <input
              type="checkbox"
              checked={areAllQuantitiesCorrect}
              onChange={() => setAreAllQuantitiesCorrect(!areAllQuantitiesCorrect)}
            />{' '}
            The total shippable received quantity is different than total expected quantity. By checking this, you
            acknowledge that all received quantities entered above are correct.
          </label>
        </div>
      );
    }
    return null;
  };

  const bypassBillOfLading = inbound.expectedInboundPackaging === 'parcel_receive';
  const quantityCheck = areAllQuantitiesCorrect || isShippableSameToExpected;

  return (
    <div>
      {mismatchQuantityCheckbox()}
      <button
        data-testid={'complete-button'}
        className="btn cta btn-complete"
        onClick={handleConfirmOrCompleteClick}
        disabled={(!isBolUploaded && !bypassBillOfLading) || !quantityCheck || disableComplete}
      >
        <i className="fa fa-check"></i>
        Complete and Finalize Inbound
      </button>
      <CompleteDropoffModal
        showModal={showModal}
        toggleModal={() => setShowModal(false)}
        onCompleteDropoff={() => completeAndFinalizeShipment()}
      />
    </div>
  );
};

export default CompleteInbound;
